"use client";

import React, { useId } from "react";
import clsx from "clsx";

import styles from "./Select.module.scss";
import FieldError, { fieldErrorBorder } from "../FieldError";
import { ChevronDown } from "react-feather";

interface SelectProps {
  options: { value: string; label: string; disabled?: boolean }[];
  value?: string;
  name: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  required?: boolean;
  testId?: string;
  error?: string;
}

interface SelectItemProps {
  children: React.ReactNode;
  className?: string;
}

/**
 *
 * Select
 *
 */
const SelectInput = ({
  options = [],
  value,
  name = "",
  onChange = (_) => {},
  className = "",
  placeholder = "",
  defaultValue = "",
  required = false,
  testId,
  error = "",
  ...props
}: SelectProps) => {
  const errorId = useId();
  const selectValueProps =
    value !== undefined ? { value } : { defaultValue: defaultValue ?? "" };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  };

  return (
    <div className={styles.select} {...props}>
      <select
        {...selectValueProps}
        data-testid={testId}
        name={name}
        onChange={handleChange}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={clsx(
          "rounded-button border border-border-color w-full h-[70px] text-left px-10 pr-14 whitespace-nowrap leading-none text-base sm:text-lg appearance-none bg-transparent",
          styles.selectElement,
          error && fieldErrorBorder,
          className,
        )}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      <span className={styles.chevron} aria-hidden>
        <ChevronDown width={24} height={24} color="currentColor" />
      </span>
      <FieldError id={errorId} message={error} />
    </div>
  );
};

export default SelectInput;
